// Submission spam gate.
//
// Two incidents (2026-08-08 and 2026-08-10) filed junk through both public
// forms from rotating Tor / VPN exits, and every one of them reported a
// conversion to Google Ads. Reported conversions train Smart Bidding, so junk
// leads do not just pollute the inbox, they teach the bidding model to buy more
// traffic that looks like the bot.
//
// Risk posture: a blocked real lead costs far more than a junk lead that gets
// through, so the tiers are deliberately lopsided.
//
//   BLOCK  - only the honeypot. A human cannot fill a field they cannot see,
//            so this has no plausible false positive. Nothing is sent anywhere.
//   FLAG   - heuristics below. The submission still goes to Google Forms so a
//            misjudged real lead is never lost, but no ad conversion fires and
//            the event is recorded as suspicious.
//
// Thresholds are tuned against the four known junk rows and every genuine lead
// in form_responses_1 / form_responses_2 as of 2026-08-10.

export type SpamVerdict = 'clean' | 'flagged' | 'blocked';

export interface SpamCheck {
  verdict: SpamVerdict;
  reasons: string[];
}

export const HONEYPOT_FIELD = 'website';

/** Signals required before a submission is treated as suspicious. */
const FLAG_THRESHOLD = 2;

/** Anything faster than this is not a person reading a form. */
const MIN_FILL_MS = 3000;

const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);

// Real leads have arrived with the state spelled out ("Florida"), so the check
// has to accept full names as well as codes. The quote form now uses a
// dropdown that only emits codes, but the contact path and any legacy
// submission can still carry free text.
const US_STATE_NAMES = new Set([
  'ALABAMA', 'ALASKA', 'ARIZONA', 'ARKANSAS', 'CALIFORNIA', 'COLORADO',
  'CONNECTICUT', 'DELAWARE', 'FLORIDA', 'GEORGIA', 'HAWAII', 'IDAHO',
  'ILLINOIS', 'INDIANA', 'IOWA', 'KANSAS', 'KENTUCKY', 'LOUISIANA', 'MAINE',
  'MARYLAND', 'MASSACHUSETTS', 'MICHIGAN', 'MINNESOTA', 'MISSISSIPPI',
  'MISSOURI', 'MONTANA', 'NEBRASKA', 'NEVADA', 'NEW HAMPSHIRE', 'NEW JERSEY',
  'NEW MEXICO', 'NEW YORK', 'NORTH CAROLINA', 'NORTH DAKOTA', 'OHIO',
  'OKLAHOMA', 'OREGON', 'PENNSYLVANIA', 'RHODE ISLAND', 'SOUTH CAROLINA',
  'SOUTH DAKOTA', 'TENNESSEE', 'TEXAS', 'UTAH', 'VERMONT', 'VIRGINIA',
  'WASHINGTON', 'WEST VIRGINIA', 'WISCONSIN', 'WYOMING',
  'DISTRICT OF COLUMBIA', 'PUERTO RICO',
]);

const isKnownState = (value: string): boolean => {
  const normalized = value.trim().toUpperCase().replace(/\s+/g, ' ');
  return US_STATES.has(normalized) || US_STATE_NAMES.has(normalized);
};

/**
 * Fraction of letters that are vowels, with accents folded first.
 *
 * Folding matters: a real lead arrived as "Avon Park" spelled with an accented
 * o, and counting that as a consonant drags a legitimate city to the threshold.
 * "y" counts as a vowel for the same reason, to keep real names clear of the
 * line while still catching keyboard mash like "Nrmykwtm" (0.13) and
 * "Lsczudmj" (0.13).
 */
const vowelRatio = (value: string): number => {
  const letters = value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  if (letters.length === 0) return 0;
  const vowels = letters.replace(/[^aeiouy]/g, '').length;
  return vowels / letters.length;
};

/**
 * Keyboard-mash test. Skips anything under 5 letters so genuinely short
 * surnames (Ng, Li, Wu) are never judged.
 */
const looksLikeMash = (value?: string | null): boolean => {
  if (!value) return false;
  const letters = value.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z]/g, '');
  if (letters.length < 5) return false;
  return vowelRatio(value) < 0.25;
};

/** A message field holding nothing but a phone number, as both junk rows did. */
const isDigitsOnly = (value?: string | null): boolean => {
  if (!value) return false;
  const stripped = value.replace(/[\s()+.-]/g, '');
  return stripped.length >= 7 && /^\d+$/.test(stripped);
};

export interface SubmissionFields {
  honeypot?: string | null;
  name?: string | null;
  city?: string | null;
  state?: string | null;
  message?: string | null;
  /** Milliseconds between the form rendering and the submit event. */
  elapsedMs?: number;
}

export const checkSubmission = (fields: SubmissionFields): SpamCheck => {
  const { honeypot, name, city, state, message, elapsedMs } = fields;

  if (honeypot && honeypot.trim().length > 0) {
    return { verdict: 'blocked', reasons: ['honeypot'] };
  }

  const reasons: string[] = [];

  if (state && state.trim().length > 0 && !isKnownState(state)) {
    reasons.push('invalid_state');
  }
  if (looksLikeMash(name)) reasons.push('name_mash');
  if (looksLikeMash(city)) reasons.push('city_mash');
  if (isDigitsOnly(message)) reasons.push('message_digits_only');
  if (typeof elapsedMs === 'number' && elapsedMs >= 0 && elapsedMs < MIN_FILL_MS) {
    reasons.push('too_fast');
  }

  return {
    verdict: reasons.length >= FLAG_THRESHOLD ? 'flagged' : 'clean',
    reasons,
  };
};
